import { api } from '@goaltree/backend/convex/_generated/api'
import {
  coachNamesForVoiceChange,
  renameCoachName,
  validateCoachName,
} from '@goaltree/backend/convex/lib/coach'
import type { OptimisticLocalStore } from 'convex/browser'

type Prefs = NonNullable<ReturnType<typeof getPrefs>>

function getPrefs(store: OptimisticLocalStore) {
  return store.getQuery(api.preferences.getMyPreferences, {})
}

export function patchPrefs(store: OptimisticLocalStore, patch: Record<string, unknown>) {
  const cur = getPrefs(store)
  if (!cur) return
  store.setQuery(api.preferences.getMyPreferences, {}, { ...cur, ...patch } as Prefs)
}

export function optimisticSetLocale(store: OptimisticLocalStore, { locale }: { locale: string }) {
  patchPrefs(store, { language: locale })
}

export function optimisticSetCoachLook(
  store: OptimisticLocalStore,
  { style }: { style: 'illustrated' | 'realistic' },
) {
  patchPrefs(store, { coachAvatarStyle: style })
}

export function optimisticClearCustomVoice(store: OptimisticLocalStore) {
  const cur = getPrefs(store)
  if (!cur) return
  const voice = (cur as { coachVoice?: string }).coachVoice
  const leavingCustom = voice === 'custom'
  patchPrefs(store, {
    customVoiceId: undefined,
    ...(leavingCustom ? { coachVoice: 'eve' } : {}),
    ...coachNamesForVoiceChange(cur, leavingCustom ? 'eve' : voice),
  })
}

export function optimisticSetHandle(store: OptimisticLocalStore, { handle }: { handle: string }) {
  if (!/^[a-z0-9_-]{3,30}$/.test(handle)) return
  store.setQuery(api.preferences.getMyHandle, {}, handle)
  patchPrefs(store, { handle })
}

export function optimisticSetBio(store: OptimisticLocalStore, { bio }: { bio: string }) {
  const next = bio.slice(0, 200)
  if (store.getQuery(api.preferences.getMyBio, {}) !== undefined) {
    store.setQuery(api.preferences.getMyBio, {}, next)
  }
  patchPrefs(store, { bio: next })
}

export function optimisticSetCoachName(
  store: OptimisticLocalStore,
  { name }: { name: string | null },
) {
  const r = validateCoachName(name)
  if (r.ok) patchPrefs(store, renameCoachName(getPrefs(store), r.name))
}
